import {Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverCoordinator} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {UserTheme, useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {inboxHasColor} from '../../../helpers/modelHelpers';
import {InboxModel} from '../../../models/api/inboxModel';
import {buildPublicId} from '../../../models/applications/publicIdTypes';
import {
  createFormFieldHoverStyle,
  createFormInputFieldHeight,
} from '../../accounts/createAccount/createAccountStyles';
import {ColorDot} from '../colorDot';
import {SearchInboxCustomFieldDropdown} from '../dropdowns/search/searchCustomFieldInboxDropdown';
import {CreateFormInputDisplay} from './createFormInputDisplay';
import {addCreateFormInputDisabledStyles} from './createFormInputHelpers';

/*
 * Props.
 */

interface InboxPopoverProps {
  title: ReactNode;
  inbox: InboxModel | undefined;
  inboxPublicId: string;
  onSelect: HandlerOf<string>;
  onDelete?: Handler;
  isModalInitiallyOpen?: boolean;
  shouldShowDeleteOptionInDropdown?: boolean;
  isDisabled?: boolean;
}

/*
 * Style.
 */

interface StyledButtonDivProps {
  $isDisabled?: boolean;
}

const StyledButtonDiv = styled.div<StyledButtonDivProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: ${(p) => p.theme.greys.shade20};
  border-radius: 8px;
  padding: 5px 8px;
  width: fit-content;
  height: ${createFormInputFieldHeight}px;

  ${createFormFieldHoverStyle};
  ${(p) => addCreateFormInputDisabledStyles(p.$isDisabled)}
`;

const StyledPlaceholderTextSpan = styled.span`
  color: ${(p) => p.theme.greys.shade60};
  white-space: nowrap;
`;

const StyledInboxNameSpan = styled.span`
  color: ${(p) => p.theme.greys.shade90};
  white-space: nowrap;
`;

const StyledButtonContentDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/*
 * Component.
 */

export const InboxPopover: FC<InboxPopoverProps> = (props) => {
  const theme = useUserTheme();
  const {
    title,
    inbox,
    shouldShowDeleteOptionInDropdown,
    isDisabled,
    onSelect,
    onDelete,
    isModalInitiallyOpen,
  } = props;

  const onChange = (inboxParameter: InboxModel) => {
    onSelect(buildPublicId('inbox', inboxParameter.id));
  };

  return (
    <CreateFormInputDisplay
      title={title}
      input={
        <PopoverCoordinator
          isDisabled={isDisabled}
          renderButton={() => (
            <StyledButtonDiv $isDisabled={isDisabled}>
              {renderInboxOrPlaceholder(inbox, theme)}
              <Icon name="chevronDownSmall" color={theme.greys.shade60} />
            </StyledButtonDiv>
          )}
          renderModal={(popoverProps) => (
            <SearchInboxCustomFieldDropdown
              {...popoverProps}
              selectedInboxId={inbox?.id}
              onChange={onChange}
              onDelete={onDelete}
              shouldShowDeleteOption
            />
          )}
          isModalInitiallyOpen={Boolean(isModalInitiallyOpen)}
        />
      }
      onDelete={shouldShowDeleteOptionInDropdown ? undefined : onDelete}
    />
  );
};

function renderInboxOrPlaceholder(inbox: InboxModel | undefined, theme: UserTheme) {
  if (inbox) {
    const color = inboxHasColor(inbox) ? inbox.color : theme.greys.shade50;
    return (
      <StyledButtonContentDiv>
        <ColorDot color={color} size={8} />
        <StyledInboxNameSpan>{inbox.name}</StyledInboxNameSpan>
      </StyledButtonContentDiv>
    );
  }

  return (
    <StyledButtonContentDiv>
      <Icon name="inboxSmall" color={theme.greys.shade60} size={16} />
      <StyledPlaceholderTextSpan>
        <Trans id="create-form-inbox-popover-select-inbox">Select inbox</Trans>
      </StyledPlaceholderTextSpan>
    </StyledButtonContentDiv>
  );
}
